/** @jsxImportSource @emotion/react */
import { cx } from '@emotion/css';
import { forwardRef, useId } from 'react';
import type { ForwardedRef, ReactElement, ReactNode } from 'react';
import Select, { components } from 'react-select';
import type { GroupBase, Props as SelectProps, ValueContainerProps } from 'react-select';
import AsyncSelect from 'react-select/async';
import type { AsyncProps } from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import type { CreatableProps } from 'react-select/creatable';
import { useTheme } from '@emotion/react';
import { useIntl, defineMessages } from 'react-intl';
import { setAlpha } from '../../utils/color-utils';
import ODSLabel from '../ODSLabel';
import ODSGroup from '../ODSGroup';
import Icon from '../Icon';

const messages = defineMessages({
  select: { id: 'ODSSelect.select', defaultMessage: 'Select…' },
  noOptions: { id: 'ODSSelect.noOptions', defaultMessage: 'No options' },
});

type ODSSelectAdditionalProps<
  Option = unknown,
  Group extends GroupBase<Option> = GroupBase<Option>
> = {
  /** Use the async variant of react-select; pair with `loadOptions` */
  async?: boolean;
  borderless?: boolean;
  cacheOptions?: AsyncProps<Option, boolean, Group>['cacheOptions'];
  className?: string;
  /** For use with ODSGrid, set the number of columns */
  columns?: number;
  creatable?: boolean;
  defaultOptions?: AsyncProps<Option, boolean, Group>['defaultOptions'];
  /** Make input full width, no rounded corners */
  fullWidth?: boolean;
  /** An id is already autogenerated, but if you want to override it, you can do so here */
  id?: string;
  invalid?: boolean;
  /** Add a label if this is used as a form field */
  label?: ReactNode;
  /** Set input bg to light */
  light?: boolean;
  loadOptions?: AsyncProps<Option, boolean, Group>['loadOptions'];
  maxHeight?: string;
  maxWidth?: string;
  minWidth?: string;
  name?: string;
  plaintext?: boolean;
  /** Display the magnifying glass icon in the input */
  search?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Pass in a testid (NOTE: currently set as an id on the wrapper div due to react-select not supporting data-* attributes; must not be the same as the id if both are provided) */
  testSelector?: string;
};

type ODSSelectNumberOption = {
  label: ReactNode;
  value: number;
};

type ODSSelectStringOption = {
  label: ReactNode;
  value: string;
};

type ODSSelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> =
  | (ODSSelectAdditionalProps<Option, Group> &
      SelectProps<Option, IsMulti, Group> & {
        creatable?: false;
      })
  | (ODSSelectAdditionalProps<Option, Group> &
      CreatableProps<Option, IsMulti, Group> & {
        creatable: true;
      });

const ValueContainer = ({ children, ...props }: ValueContainerProps) => {
  return (
    components.ValueContainer && (
      <components.ValueContainer {...props}>
        {!!children && (
          <Icon
            name="search"
            size="sm"
            className="text-secondary position-absolute"
            fixedWidth
            style={{ width: '1.75rem', left: 0 }}
          />
        )}
        {children}
      </components.ValueContainer>
    )
  );
};

function ODSSelectInner<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  {
    async: isAsync,
    creatable,
    plaintext,
    invalid,
    className,
    borderless,
    size = 'sm',
    noOptionsMessage,
    placeholder,
    minWidth,
    maxWidth,
    maxHeight,
    menuPlacement = 'auto',
    menuPortalTarget = document.body,
    maxMenuHeight,
    label,
    id,
    testSelector,
    columns,
    search,
    fullWidth,
    light,
    components: selectComponents,
    styles = {},
    ...componentProps
  }: ODSSelectProps<Option, IsMulti, Group>,
  ref: ForwardedRef<any>
): ReactElement {
  const intl = useIntl();
  const SelectTag = (creatable ? CreatableSelect : isAsync ? AsyncSelect : Select) as any;
  const theme = useTheme();
  const uid = useId();
  const selectID = id ?? uid;
  const defaultPlaceholder = intl.formatMessage(messages.select);
  const defaultNoOptionsMessage = () => intl.formatMessage(messages.noOptions);

  let bgColor: string = theme.colors.white;

  if (light) bgColor = theme.colors.light;
  if (componentProps.isDisabled) bgColor = theme.colors.disabled;

  const reactSelect = (
    <SelectTag
      ref={ref}
      {...componentProps}
      className={cx(
        'react-select',
        'w-100',
        { 'react-select--borderless': borderless },
        { 'react-select--sm': size === 'sm' },
        { 'react-select--plaintext': plaintext },
        { 'is-invalid': invalid },
        className
      )}
      classNamePrefix="react-select"
      // react-select doesn't yet support data-* attributes, see https://github.com/JedWatson/react-select/pull/6074
      id={testSelector !== id ? testSelector : undefined}
      noOptionsMessage={noOptionsMessage ?? defaultNoOptionsMessage}
      placeholder={placeholder ?? defaultPlaceholder}
      menuPlacement={menuPlacement}
      menuPortalTarget={menuPortalTarget}
      maxMenuHeight={maxMenuHeight ?? window.innerHeight / 2}
      inputId={selectID}
      captureMenuScroll
      components={{ ...(search && { ValueContainer }), ...selectComponents }}
      styles={{
        ...styles,
        groupHeading: (provided: any) => ({
          ...provided,
          color: theme.colors.secondary,
          fontSize: '0.625rem',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontweight: 500,
          ...(styles as any).groupHeading?.(provided),
        }),
        control: (provided: any) => ({
          ...provided,
          '&, &:focus, &:focus-within, &--is-focused': {
            backgroundColor: `${bgColor} !important`,
          },
          ...(fullWidth && {
            border: 'none',
            borderRadius: 0,
            '&:focus, &:focus-within, &--is-focused': {
              boxShadow: `none !important`,
            },
          }),
          ...(styles as any).control?.(provided),
        }),
        valueContainer: (provided: any) => ({
          ...provided,
          flexWrap: 'nowrap',
          display: 'flex',
          position: 'relative',
          paddingLeft: search ? ' 1.75rem !important' : '0.5rem !important',
          fontSize: theme.text.size.small,
          ...(styles as any).valueContainer?.(provided),
        }),
        placeholder: (provided: any) => ({
          ...provided,
          color: theme.colors.placeholder,
          position: 'absolute',
          left: search ? '1.75rem' : '0.5rem',
          right: '0.5rem',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          ...(styles as any).placeholder?.(provided),
        }),
        input: (provided: any) => ({
          ...provided,
          margin: 0,
          padding: 0,
          ...(styles as any).input?.(provided),
        }),
        option: (provided: any, { disabled }) => ({
          ...provided,
          fontSize: theme.text.size.small,
          paddingTop: '0.5em',
          paddingBottom: '0.5em',
          ...(disabled && {
            color: setAlpha(theme.colors.black, 0.3),
            pointerEvent: 'none',
            backgroundColor: 'transparent !important',
          }),
          ...(styles as any).option?.(provided),
        }),
        menu: (provided: any) => ({
          ...provided,
          ...(!!minWidth && { minWidth }),
          ...(!!maxWidth && { maxWidth }),
          ...(!!maxHeight && { maxHeight }),
          ...(styles as any).menu?.(provided),
        }),
        multiValue: (provided: any) => ({
          ...provided,
          padding: 0,
          border: 'none',
          background: theme.darken(theme.colors.light, 0.05),
          color: theme.colors.secondary,
          ...(styles as any).multiValue?.(provided),
        }),
        multiValueLabel: (provided: any) => ({
          ...provided,
          fontWeight: 500,
          paddingLeft: '0.5em',
          paddingRight: '0.5em',
          ...(styles as any).multiValueLabel?.(provided),
        }),
        multiValueRemove: (provided: any) => ({
          ...provided,
          paddingLeft: '0.25em',
          paddingRight: '0.25em',
          ...(componentProps.isDisabled ? { display: 'none !important' } : { cursor: 'pointer' }),
          ...(styles as any).multiValueRemove?.(provided),
        }),
        singleValue: (provided: any) => ({
          ...provided,
          ...(styles as any).singleValue?.(provided),
        }),
      }}
    />
  );

  if (label) {
    return (
      <ODSGroup stacked spacing="sm" {...(!!columns && { columns })}>
        <ODSLabel htmlFor={selectID}>{label}</ODSLabel>
        {reactSelect}
      </ODSGroup>
    );
  }

  return reactSelect;
}

const ODSSelect = forwardRef(ODSSelectInner) as <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props: ODSSelectProps<Option, IsMulti, Group> & {
    ref?: ForwardedRef<any>;
  }
) => ReactElement;

export type { ODSSelectProps, ODSSelectNumberOption, ODSSelectStringOption };
export default ODSSelect;
